import { getRecipientDonorColumns } from 'constants/tables';
import dayjs from 'dayjs';
import { useAppSelector } from 'helpers/hooks';
import { useMemo } from 'react';
import { getActiveRole, getSelectedRecipientId } from 'selectors/admins';
import { getUserClientId } from 'selectors/user';
import {
  getRecipientDonationListColumnVisibility,
  getRecipientDonationListSearchString,
  getRecipientDonationListSortState,
  getUserDates,
} from 'selectors/viewPreferences';
import { useDonorsData } from 'hooks/useDonorsData';
import {
  recipientDonationListColumnVisibility,
  recipientDonationListSearchString,
  recipientDonationListSortState,
} from 'store/viewPreferences/slice';
import DonationDetailContent from 'views/DonationDetail/DonationDetailContent';
import DonorList from 'views/DonationDetail/donorList';
import { DashboardScreen } from 'helpers/mixpanelEvents';

const sessionStorageKey = 'recipientDonationDetailActiveIndex';
const SCREEN: DashboardScreen = 'recipient_donation_detail';

const RecipientDonationDetail = () => {
  const activeRole = useAppSelector(getActiveRole);
  const userDates = useAppSelector(getUserDates);
  const userClientId = useAppSelector(getUserClientId);
  const recipientId = useAppSelector(getSelectedRecipientId);

  const startDate = userDates?.start
    ? dayjs(userDates.start).startOf('day').toISOString()
    : undefined;
  const endDate = userDates?.end ? dayjs(userDates.end).endOf('day').toISOString() : undefined;

  // client_id is intentionally omitted: the recipient-scoped endpoint is
  // already scoped by recipient_id and never filtered by client.
  const {
    donors,
    isLoading: donorsLoading,
    data,
  } = useDonorsData(
    { recipient_id: recipientId ?? '', start_date: startDate ?? '', end_date: endDate ?? '' },
    !activeRole || !recipientId || !startDate || !endDate
  );
  const donorsState = { isLoading: donorsLoading };
  const isEmployerAddressRequired = data?.is_employer_address_required ?? false;

  const prefSelectors = useMemo(
    () => ({
      columnVisibility: getRecipientDonationListColumnVisibility,
      searchString: getRecipientDonationListSearchString,
      sortState: getRecipientDonationListSortState,
    }),
    []
  );

  const prefActions = useMemo(
    () => ({
      setColumnVisibility: recipientDonationListColumnVisibility,
      setSearchString: recipientDonationListSearchString,
      setSortState: recipientDonationListSortState,
    }),
    []
  );

  const columns = useMemo(
    () => getRecipientDonorColumns(isEmployerAddressRequired),
    [isEmployerAddressRequired]
  );

  const tabs = useMemo(
    () => [
      {
        label: 'By Donation',
        view: (
          <DonorList
            donors={donors}
            columns={columns}
            sessionStoragePrefix="recipientDonationList"
            screen={SCREEN}
            prefSelectors={prefSelectors}
            prefActions={prefActions}
          />
        ),
      },
    ],
    [donors, columns, prefSelectors, prefActions]
  );

  return (
    <DonationDetailContent
      activeRole={activeRole}
      donors={donors}
      donorsState={donorsState}
      permissionKey="RECIPIENTS_DONATION_LIST_ROUTE"
      screen={SCREEN}
      sessionStorageKey={sessionStorageKey}
      tabs={tabs}
      userClientId={userClientId}
      userDates={userDates}
    />
  );
};

export default RecipientDonationDetail;
